import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight, AlertTriangle, Info, OctagonAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDate, num } from "@/lib/format";
import { label } from "@/lib/labels";

/* ------------------------------------------------------------------ status grammar (design_guidelines → room_map_grammar) ---- */

export const ROOM_STATUS_ORDER = ["ledig_klar", "opptatt", "reservert", "ma_rengjores", "ute_av_drift", "ikke_aktiv"];

/** Dot colour carries the meaning; tiles stay neutral so a 160-room grid reads calmly. */
export const ROOM_DOT = {
  ledig_klar: "bg-emerald-500",
  opptatt: "bg-foreground/70",
  reservert: "bg-amber-500",
  ma_rengjores: "bg-amber-500",
  ute_av_drift: "bg-rose-500",
  ikke_aktiv: "bg-border",
};
export const ROOM_TILE = {
  ledig_klar: "bg-emerald-500/[0.07] border-emerald-500/30",
  opptatt: "bg-foreground/[0.06] border-foreground/20",
  reservert: "bg-amber-500/[0.08] border-amber-500/30",
  ma_rengjores: "bar-planned bg-amber-500/[0.06] border-amber-500/40 border-dashed",
  ute_av_drift: "bg-rose-500/[0.07] border-rose-500/40",
  ikke_aktiv: "bg-muted/40 border-border/60",
};
export const roomStatusLabel = (s) => label(s);

export const dayLabel = (d) => (d ? fmtDate(d, "d. MMM") : "–");
const ucfirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
export const dayLabelLong = (d) => (d ? ucfirst(fmtDate(d, "EEEE d. MMMM")) : "–");
export const timeLabel = (iso) => (iso ? fmtDate(iso, "HH:mm") : "");
export const weekOf = (d) => (d ? `U${fmtDate(d, "I")}` : "");

/* ------------------------------------------------------------------ lens facts ---- */

const TONE_TEXT = { success: "text-emerald-700 dark:text-emerald-300", warning: "text-amber-700 dark:text-amber-300", danger: "text-rose-600 dark:text-rose-400", muted: "text-muted-foreground" };

export const Fact = ({ label: l, value, sub, tone, testId, size = "md", className, onClick, mono = true }) => {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp type={onClick ? "button" : undefined} onClick={onClick} className={cn("min-w-0 text-left", onClick && "rounded-md -mx-1 px-1 transition-colors duration-150 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)} data-testid={testId}>
      <div className={cn("truncate font-semibold tracking-tight tabular", mono ? "font-mono" : "font-display", size === "lg" ? "text-[28px] leading-9 sm:text-[32px] sm:leading-10" : "text-[22px] leading-7 sm:text-[26px] sm:leading-8", TONE_TEXT[tone])} data-testid={testId ? `${testId}-value` : undefined}>
        {value ?? "–"}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div>
      {sub && <div className="mt-0.5 truncate text-[11.5px] leading-4 text-muted-foreground/90">{sub}</div>}
    </Comp>
  );
};

/** Muted track + graphite fill (no colour until it matters). */
export const OccupancyBar = ({ pct, tone, className, testId }) => {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const fill = tone === "danger" ? "bg-rose-500" : tone === "warning" ? "bg-amber-500" : "bg-foreground/70";
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)} role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100} data-testid={testId}>
      <div className={cn("h-full rounded-full transition-transform duration-200 origin-left", fill)} style={{ width: `${p}%` }} />
    </div>
  );
};

/* ------------------------------------------------------------------ brief + alerts ---- */

export const AccommodationBrief = ({ sentences = [], className, testId = "accommodation-brief", compact, meta = "Beregnet deterministisk fra rom, plasseringer, crew-rotasjoner og opphold" }) => {
  if (!sentences.length) return null;
  return (
    <div className={cn("relative pl-7", className)} data-testid={testId}>
      <Sparkles className="absolute left-0 top-[4px] h-4 w-4 text-primary" strokeWidth={1.75} aria-hidden />
      <p className={cn("text-foreground/90", compact ? "text-[13.5px] leading-6" : "text-[15px] leading-7 sm:text-[16px]")} data-testid={`${testId}-text`}>
        {sentences.map((s, i) => (
          <span key={i} className={cn(i === 0 && "font-medium text-foreground")}>
            {s}{" "}
          </span>
        ))}
      </p>
      {meta && <p className="mt-1 text-[11.5px] leading-4 text-muted-foreground">{meta}</p>}
    </div>
  );
};

const ALERT_ICON = { danger: OctagonAlert, warn: AlertTriangle, info: Info };
const ALERT_TONE = { danger: "text-rose-600 dark:text-rose-400", warn: "text-amber-700 dark:text-amber-300", info: "text-muted-foreground" };

export const AlertList = ({ alerts = [], className, testId = "accommodation-alerts" }) => {
  if (!alerts.length) return null;
  return (
    <ul className={cn("space-y-1.5", className)} data-testid={testId}>
      {alerts.map((a, i) => {
        const Icon = ALERT_ICON[a.severity] || Info;
        return (
          <li key={i} className="flex items-start gap-2 text-[13px] leading-5" data-testid={`${testId}-item-${a.kind}`}>
            <Icon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", ALERT_TONE[a.severity])} />
            <span className="text-foreground/85">{a.text}</span>
          </li>
        );
      })}
    </ul>
  );
};

/* ------------------------------------------------------------------ small list rows ---- */

export const RoomCode = ({ operational, physical, className }) => (
  <span className={cn("inline-flex items-baseline gap-1.5", className)}>
    <span className="font-semibold tabular">{operational || "–"}</span>
    {physical && <span className="font-mono text-[11px] text-muted-foreground">{physical}</span>}
  </span>
);

export const PersonLine = ({ name, employer, crew, hidden, className }) => (
  <span className={cn("min-w-0", className)}>
    <span className="block truncate text-[13px] font-medium leading-5">{hidden ? "Person (skjult)" : name || "–"}</span>
    {(employer || crew) && <span className="block truncate text-[11.5px] leading-4 text-muted-foreground">{[employer, crew].filter(Boolean).join(" · ")}</span>}
  </span>
);

export const StatusDot = ({ status, className }) => <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", ROOM_DOT[status] || "bg-border", className)} aria-hidden />;

export const Legend = ({ counts, className, onPick, active }) => (
  <ul className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground", className)} data-testid="room-map-legend">
    {ROOM_STATUS_ORDER.filter((k) => k !== "ikke_aktiv" || counts?.[k]).map((k) => (
      <li key={k}>
        <button type="button" onClick={() => onPick?.(k)} className={cn("inline-flex items-center gap-1.5 rounded px-1 -mx-1 transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", active === k && "text-foreground font-medium")} data-testid={`room-map-legend-${k}`}>
          <StatusDot status={k} /> {roomStatusLabel(k)}
          {counts && <span className="tabular font-mono text-[11px]">{num(counts[k] || 0)}</span>}
        </button>
      </li>
    ))}
  </ul>
);

export const QuietLink = ({ to, onClick, children, testId, className }) => {
  const cls = cn("inline-flex shrink-0 items-center gap-1 text-[12px] text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:text-foreground focus-visible:outline-none", className);
  if (to)
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {children} <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    );
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testId}>
      {children} <ArrowUpRight className="h-3.5 w-3.5" />
    </button>
  );
};

export const accommodationUrl = (projectId, view) => `/prosjekter/${projectId}?tab=innkvartering${view ? `&view=${view}` : ""}`;
